import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import './movie-list.scss';

import { SwiperSlide, Swiper } from 'swiper/react';
import { FaChevronLeft, FaChevronRight, FaPlay } from 'react-icons/fa';

import MovieCard from '../movie-card/MovieCard';
import { encryptId } from '../SecurityComponent/cryptoUtils';

// ── Lightweight card variants ──────────────────────────────────

const HeroCard = ({ item }) => (
    <Link to={`/watch/${item.slug}/${encryptId(1)}`} className="ml-hero-card">
        <div className="ml-hero-card__bg" style={{ backgroundImage: `url(${item.backdrop_path || item.poster_path})` }} />
        <div className="ml-hero-card__overlay" />
        <div className="ml-hero-card__info">
            <h3 className="ml-hero-card__title">{item.title}</h3>
            <div className="ml-hero-card__meta">
                {item.releaseYear && <span>{item.releaseYear}</span>}
                {item.episodeCount > 1 && <span>{item.episodeCount} tập</span>}
                {item.averageRating > 0 && <span>★ {Number(item.averageRating).toFixed(1)}</span>}
            </div>
            <div className="ml-hero-card__btn">▶ Xem ngay</div>
        </div>
    </Link>
);

const HorizontalCard = ({ item, rank }) => (
    <Link to={`/watch/${item.slug}/${encryptId(1)}`} className="ml-h-card">
        <span className="ml-h-card__rank">{rank}</span>
        <div className="ml-h-card__thumb" style={{ backgroundImage: `url(${item.backdrop_path || item.poster_path})` }} />
        <div className="ml-h-card__info">
            <p className="ml-h-card__title">{item.title}</p>
            <p className="ml-h-card__meta">
                {[item.releaseYear, item.episodeCount > 1 ? `${item.episodeCount} tập` : null].filter(Boolean).join(' · ')}
            </p>
            {item.averageRating > 0 && <p className="ml-h-card__rating">★ {Number(item.averageRating).toFixed(1)}</p>}
        </div>
    </Link>
);

const UpdateCard = ({ item, newUpdateBadge }) => (
    <Link to={`/watch/${item.slug}/${encryptId(1)}`} className="ml-update-card">
        <div className="ml-update-card__poster"
            style={{ backgroundImage: `url(${item.poster_path || item.backdrop_path})` }}>

            {/* New badge — top left */}
            <span className="ml-update-card__new-badge">
                {newUpdateBadge || 'NEW'}
            </span>

            {/* Quality — top right */}
            <span className="ml-update-card__quality">HD</span>

            {/* Hover play */}
            <div className="ml-update-card__play-wrap">
                <span className="ml-update-card__play-btn"><FaPlay size={13} /></span>
            </div>

            {/* Bottom info */}
            <div className="ml-update-card__info">
                <h3 className="ml-update-card__title">{item.title}</h3>
                <div className="ml-update-card__row">
                    {item.averageRating > 0 && (
                        <span className="ml-update-card__star">★ {Number(item.averageRating).toFixed(1)}</span>
                    )}
                    {item.episodeCount > 1 && <span className="ml-update-card__meta-txt">{item.episodeCount} tập</span>}
                    {item.releaseYear && <span className="ml-update-card__meta-txt">{item.releaseYear}</span>}
                </div>
                {item.genres?.length > 0 && (
                    <div className="ml-update-card__genres">
                        {item.genres.slice(0, 2).map(g => (
                            <span key={g.id} className="ml-update-card__genre">{g.name}</span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    </Link>
);

const MinimalCard = ({ item, rank }) => (
    <Link to={`/watch/${item.slug}/${encryptId(1)}`} className="ml-min-card">
        <span className="ml-min-card__num">{String(rank).padStart(2, '0')}</span>
        <div className="ml-min-card__bar" />
        <div className="ml-min-card__info">
            <p className="ml-min-card__title">{item.title}</p>
            <p className="ml-min-card__sub">{[item.releaseYear, item.country].filter(Boolean).join(' · ')}</p>
        </div>
        {item.averageRating > 0 && <span className="ml-min-card__score">{Number(item.averageRating).toFixed(1)}</span>}
    </Link>
);

const proxyImg = url => {
    if (!url) return url;
    if (url.includes('/ophim-img')) return url;
    if (url.startsWith('https://img.ophim.live')) return url.replace('https://img.ophim.live', '/ophim-img');
    if (url.startsWith('/')) return '/ophim-img' + url;
    if (!url.startsWith('http')) return '/ophim-img/uploads/movies/' + url;
    return url;
};

// ── Main MovieList ─────────────────────────────────────────────

const PHP_TYPE = {
    'popular':    'popular',
    'top-rated':  'toprated',
    'new-update': 'newUpdate',
    'trending':   'trending',
};

const MovieList = props => {
    const { cardStyle = 'poster', newUpdateBadge } = props;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const swiperRef = useRef(null);

    useEffect(() => {
        const phpType = PHP_TYPE[props.type] ?? 'new';
        const size = props.type === 'new-update' ? 24 : 10;
        setLoading(true);
        fetch(`/movies-public-api.php?type=${phpType}&page=${props.page}&size=${size}`)
            .then(r => r.json())
            .then(d => {
                if (d.content) setItems(d.content);
                else setError('Dữ liệu không hợp lệ');
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [props.type, props.page, props.refreshKey]);

    const updateNavState = (swiper) => {
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };

    if (loading) return <div className="movie-list-loading">Loading...</div>;
    if (error) return <div className="movie-list-error">Error: {error}</div>;

    // ── Minimal: vertical ranked list (no Swiper) ──────────────
    if (cardStyle === 'minimal') {
        return (
            <div className="movie-list ml-minimal-list">
                {items.map((item, i) => (
                    <MinimalCard key={i} item={{
                        ...item,
                        poster_path: proxyImg(item.posterUrl),
                        backdrop_path: proxyImg(item.thumbnailUrl),
                    }} rank={i + 1} />
                ))}
            </div>
        );
    }

    // ── Swiper-based layouts ───────────────────────────────────
    const isUpdateSection = props.type === 'new-update';
    const swiperClass = isUpdateSection
        ? 'movie-list movie-list--update'
        : `movie-list movie-list--${cardStyle}`;

    return (
        <div className={swiperClass}>
            {!isBeginning && (
                <button className="ml-nav ml-nav--prev" onClick={() => {
                    const swiper = swiperRef.current;
                    if (!swiper) return;
                    const step = swiper.slidesPerViewDynamic?.() || 1;
                    swiper.slideTo(Math.max(0, swiper.activeIndex - step));
                }}>
                    <FaChevronLeft />
                </button>
            )}
            {!isEnd && (
                <button className="ml-nav ml-nav--next" onClick={() => {
                    const swiper = swiperRef.current;
                    if (!swiper) return;
                    const step = swiper.slidesPerViewDynamic?.() || 1;
                    swiper.slideTo(swiper.activeIndex + step);
                }}>
                    <FaChevronRight />
                </button>
            )}
            <Swiper
                grabCursor={true}
                spaceBetween={isUpdateSection ? 14 : cardStyle === 'hero' ? 16 : cardStyle === 'horizontal' ? 12 : 25}
                slidesPerView={'auto'}
                onSwiper={(swiper) => { swiperRef.current = swiper; updateNavState(swiper); }}
                onSlideChange={(swiper) => updateNavState(swiper)}
                onActiveIndexChange={(swiper) => updateNavState(swiper)}
                onReachBeginning={(swiper) => updateNavState(swiper)}
                onReachEnd={(swiper) => updateNavState(swiper)}
            >
                {items.map((item, i) => {
                    const normalized = {
                        id: item.id,
                        title: item.title,
                        poster_path: proxyImg(item.posterUrl),
                        backdrop_path: proxyImg(item.thumbnailUrl),
                        slug: item.slug,
                        releaseYear: item.releaseYear,
                        duration: item.duration,
                        averageRating: item.averageRating,
                        episodeCount: item.episodeCount,
                        latestEpisode: item.latestEpisode,
                        lastEpisodeAt: item.lastEpisodeAt,
                        status: item.status,
                        genres: item.genres,
                        description: item.description,
                        country: item.country,
                        quality: item.publisher,
                        trailerUrl: item.trailerUrl,
                        newEpisodesCount: item.newEpisodesCount ?? 0,
                    };

                    return (
                        <SwiperSlide key={i}>
                            {isUpdateSection && <UpdateCard item={normalized} newUpdateBadge={newUpdateBadge} />}
                            {!isUpdateSection && cardStyle === 'hero' && <HeroCard item={normalized} />}
                            {!isUpdateSection && cardStyle === 'horizontal' && <HorizontalCard item={normalized} rank={i + 1} />}
                            {!isUpdateSection && !['hero', 'horizontal'].includes(cardStyle) && (
                                <MovieCard item={normalized} variant={cardStyle !== 'poster' ? cardStyle : undefined} trailerMode={normalized.episodeCount === 0} />
                            )}
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
}

MovieList.propTypes = {
    type: PropTypes.string.isRequired,
    page: PropTypes.number.isRequired,
    cardStyle: PropTypes.string,
    refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default MovieList;
